/**
 * Google Maps loader, shared by every map on the page. The API can only be
 * bootstrapped once, so the first component to ask for it owns the promise and
 * the rest await the same one.
 */
let mapsPromise = null;

function loadGoogleMaps(apiKey) {
  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise((resolve, reject) => {
    const callback = "storeMapApiReady";
    window[callback] = resolve;

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&loading=async&callback=${callback}`;
    script.async = true;
    script.onerror = () => reject(new Error("Google Maps failed to load"));

    document.head.appendChild(script);
  });

  return mapsPromise;
}

class StoreMap extends HTMLElement {
  constructor() {
    super();

    this.canvas = this.querySelector("[data-map-canvas]");
  }

  connectedCallback() {
    if (!this.canvas || !this.dataset.apiKey || !this.dataset.latlng) return;

    // The Maps API is a few hundred kilobytes, so hold off until the map is
    // close to being looked at.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.observer.disconnect();
        this.render();
      },
      { rootMargin: "200px" },
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  async render() {
    const [lat, lng] = this.dataset.latlng
      .split(",")
      .map((value) => parseFloat(value));

    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    try {
      await loadGoogleMaps(this.dataset.apiKey);
    } catch (e) {
      // Blocked or failed — drop the empty box; the address and directions link
      // beside it already cover the same ground.
      this.setAttribute("data-map-unavailable", "");
      return;
    }

    const position = { lat, lng };

    const map = new google.maps.Map(this.canvas, {
      center: position,
      zoom: parseInt(this.dataset.zoom, 10) || 16,
      mapTypeControl: false,
      // Quiet the surrounding businesses and transit clutter so the store pin
      // is the only thing competing for attention.
      styles: [
        {
          featureType: "poi.business",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "transit",
          elementType: "labels.icon",
          stylers: [{ visibility: "off" }],
        },
      ],
    });

    new google.maps.Marker({
      map: map,
      position: position,
      icon: this.dataset.pin || undefined,
      title: this.dataset.title || undefined,
    });
  }
}

customElements.define("store-map", StoreMap);
